interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface Props {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (product: Product) => void;
  onCardClick: (product: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: '#fb7185' },
  instagram: { icon: 'ri-instagram-line',         color: '#ff6b9d' },
  twitter:   { icon: 'ri-twitter-x-line',         color: '#374151' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: '#ff8c42' },
  tiktok:    { icon: 'ri-tiktok-line',            color: '#374151' },
  youtube:   { icon: 'ri-youtube-line',           color: '#ff6b9d' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: '#ff8c42' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: '#fb7185' },
  telegram:  { icon: 'ri-telegram-line',          color: '#ff8c42' },
  custom:    { icon: 'ri-links-line',             color: '#ff6b9d' },
};

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

export default function VibrantTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: Props) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen font-sans" style={{ background: '#fff5f7' }}>
      {/* Hero */}
      <div className="relative w-full overflow-hidden" style={{ background: 'linear-gradient(135deg, #ff6b9d 0%, #ff8c42 50%, #ffd166 100%)', minHeight: '280px' }}>
        {storeProfile?.banner_image && (
          <img src={storeProfile.banner_image} alt="banner" className="absolute inset-0 w-full h-full object-cover object-top opacity-30" />
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 flex flex-col items-center text-center">
          {(storeProfile?.logo_image || userProfile?.avatar_url) && (
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-white shadow-xl mb-4">
              <img src={storeProfile?.logo_image || userProfile?.avatar_url || ''} alt="logo" className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="text-4xl font-black text-white mb-3 drop-shadow-sm">{storeName}</h1>
          <p className="text-white/90 text-base max-w-lg leading-relaxed">{storeDesc}</p>
          <div className="flex items-center gap-4 mt-5">
            <span className="px-4 py-1.5 rounded-full text-sm font-bold bg-white/20 text-white backdrop-blur-sm">
              {products.length} Products
            </span>
            {digitalCount > 0 && (
              <span className="px-4 py-1.5 rounded-full text-sm font-bold bg-white/20 text-white backdrop-blur-sm">
                {digitalCount} Digital
              </span>
            )}
            {physicalCount > 0 && (
              <span className="px-4 py-1.5 rounded-full text-sm font-bold bg-white/20 text-white backdrop-blur-sm">
                {physicalCount} Physical
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Linktree Section */}
        {activeLinks.length > 0 && (
          <div className="mb-8 rounded-2xl px-6 py-5" style={{ background: '#fff', border: '2px solid #ffd6e0' }}>
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-links-line text-sm text-rose-400"></i>
              <h3 className="text-xs font-bold uppercase tracking-widest text-rose-300">Links</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all group cursor-pointer"
                    style={{ background: '#fff5f7', border: '2px solid #ffd6e0' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b9d'; e.currentTarget.style.background = '#ffe4ec'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#ffd6e0'; e.currentTarget.style.background = '#fff5f7'; }}
                  >
                    <i className={`${meta.icon} text-sm`} style={{ color: meta.color }}></i>
                    <span className="text-xs font-bold" style={{ color: '#ff6b9d' }}>{link.label}</span>
                    <i className="ri-external-link-line text-xs text-rose-200"></i>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-rose-300"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm outline-none"
              style={{ background: '#fff', border: '2px solid #ffd6e0', color: '#333' }}
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className="px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                style={activeFilter === f
                  ? { background: 'linear-gradient(135deg, #ff6b9d, #ff8c42)', color: '#fff', boxShadow: '0 4px 12px rgba(255,107,157,0.4)' }
                  : { background: '#fff', color: '#ff6b9d', border: '2px solid #ffd6e0' }}
              >
                {f === 'all' ? `All (${products.length})` : f === 'digital' ? `Digital (${digitalCount})` : `Physical (${physicalCount})`}
              </button>
            ))}
          </div>
        </div>

        {/* Products */}
        {filteredProducts.length === 0 ? (
          <div className="py-24 text-center">
            <i className="ri-store-2-line text-5xl mb-4 block text-rose-200"></i>
            <p className="text-lg font-bold text-rose-300">{searchQuery ? 'Nothing found' : 'No products yet'}</p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap text-white" style={{ background: 'linear-gradient(135deg, #ff6b9d, #ff8c42)' }}>
                Clear
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-16">
            {filteredProducts.map(product => {
              const isPhysical = product.product_type === 'physical';
              const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
              const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
              const src = firstMedia ? firstMedia.url : product.cover_image;
              const isVid = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
              return (
                <div
                  key={product.id}
                  onClick={() => onCardClick(product)}
                  className="bg-white rounded-2xl overflow-hidden flex flex-col cursor-pointer group transition-all duration-300 hover:-translate-y-1"
                  style={{ boxShadow: '0 4px 20px rgba(255,107,157,0.1)', border: '2px solid #ffd6e0' }}
                >
                  <div className="relative overflow-hidden flex-shrink-0" style={{ minHeight: '180px', background: '#fff5f7' }}>
                    {src ? (
                      isVid ? (
                        <video src={src} className="w-full h-auto object-contain" muted autoPlay loop playsInline />
                      ) : (
                        <img src={src} alt={product.title} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                      )
                    ) : (
                      <div className="w-full h-44 flex flex-col items-center justify-center">
                        <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-5xl text-rose-200 mb-2`}></i>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{ background: isPhysical ? '#ff8c42' : 'linear-gradient(135deg, #ff6b9d, #ff8c42)' }}>
                        {isPhysical ? 'Physical' : 'Digital'}
                      </span>
                    </div>
                    {outOfStock && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <span className="text-sm font-bold text-rose-300 border-2 border-rose-200 px-4 py-1.5 rounded-full">Out of Stock</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 mb-1">{product.title}</h3>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3 flex-1">{product.description}</p>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-rose-50">
                      <span className="text-lg font-extrabold" style={{ background: 'linear-gradient(135deg, #ff6b9d, #ff8c42)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {product.currency} {product.price.toLocaleString()}
                      </span>
                      <button
                        onClick={e => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                        disabled={outOfStock}
                        className="px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap text-white"
                        style={outOfStock ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : { background: 'linear-gradient(135deg, #ff6b9d, #ff8c42)', boxShadow: '0 4px 12px rgba(255,107,157,0.3)' }}
                      >
                        <i className={isPhysical ? 'ri-shopping-cart-line' : 'ri-download-line'}></i>
                        {isPhysical ? 'Buy' : 'Get'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8" style={{ background: 'linear-gradient(135deg, #ff6b9d, #ff8c42)' }}>
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm font-bold text-white/90">Powered by <strong className="text-white">EventHub Store</strong></span>
          <div className="flex items-center gap-4 text-xs text-white/80">
            <span className="flex items-center gap-1"><i className="ri-shield-check-line"></i> Secure</span>
            <span className="flex items-center gap-1"><i className="ri-lock-line"></i> Protected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
